import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppContext } from '@/contexts/AppContext';
import { supabase } from '@/lib/supabase';
import { User, Handshake, ShieldCheck } from 'lucide-react';
import eightWireIqLogo from '@/assets/8wireiq-logo.webp';
import {
  DEFAULT_PLATFORM,
  applyDocumentBranding,
  applyTheme,
  OPSLINK_ICON,
  cachedPlatformBranding,
  loadPlatformBranding,
  rememberTenantBranding,
  resolveTenantByCode,
  type PlatformBranding,
  type TenantBranding,
} from '@/lib/branding';

interface LoginFormProps {
  onLogin: (userData: { name: string; role: 'driver' | 'admin'; vehicleId?: string }) => void;
}

interface TenantDriverOption {
  driver_name: string;
  is_admin: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const { drivers, login, loginPortalUser, loginSuperadmin } = useAppContext();

  // Deliberately reached via a small, unlabelled icon (bottom-right of
  // every screen this form renders) rather than a fourth tile next to
  // Driver/Partner/Admin -- Janelle's own call: an obvious "Superadmin"
  // option just invites regular users to try logging in through it.
  // AppLayout reacts to isSuperadmin becoming true on its own once
  // loginSuperadmin succeeds, so there's nothing else to wire up here.
  const [superadminMode, setSuperadminMode] = useState(false);
  const [superadminEmail, setSuperadminEmail] = useState('');
  const [superadminPassword, setSuperadminPassword] = useState('');
  const [superadminError, setSuperadminError] = useState('');
  const [superadminSubmitting, setSuperadminSubmitting] = useState(false);

  const [tenantCode, setTenantCode] = useState('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  // Before a company code is entered the screen shows the platform's own branding; once it is, that
  // company's own name, logo and colours take over (see lib/branding.ts).
  const [platform, setPlatform] = useState<PlatformBranding>(() => cachedPlatformBranding() ?? DEFAULT_PLATFORM);
  const [tenantBrand, setTenantBrand] = useState<TenantBranding | null>(null);
  useEffect(() => {
    let alive = true;
    loadPlatformBranding().then((p) => {
      if (!alive) return;
      setPlatform(p);
      applyDocumentBranding({ title: p.product_name, faviconUrl: p.favicon_url });
    });
    return () => {
      alive = false;
    };
  }, []);
  const [tenantDrivers, setTenantDrivers] = useState<TenantDriverOption[]>([]);
  const [tenantError, setTenantError] = useState('');
  const [resolvingTenant, setResolvingTenant] = useState(false);

  // Three login types, chosen up front (like a delivery-style role picker)
  // instead of a driver picking "Admin" from within their own form:
  // "driver" and "admin" both use the driver-name + password flow, just
  // scoped to a different slice of the roster (see driverOptions below);
  // "partner" is the non-driver portal login (e.g. a lease operator's
  // family member helping with bookwork) -- email + password instead of
  // picking a name from the roster.
  const [loginMode, setLoginMode] = useState<'driver' | 'partner' | 'admin'>('driver');
  const [name, setName] = useState('');
  const [portalEmail, setPortalEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = tenantCode.trim();
    if (!code) return;

    setResolvingTenant(true);
    setTenantError('');

    const tenant = await resolveTenantByCode(code);
    const tenantLookupError = null;

    if (tenantLookupError || !tenant) {
      setTenantError("Company code not recognised. Check with your admin and try again.");
      setResolvingTenant(false);
      return;
    }

    // Runs as anon -- nobody's authenticated yet, that's the whole point
    // of a name-picker login. drivers itself is real-RLS-locked (bank
    // details, insurance history, etc.), so this goes through a narrow
    // SECURITY DEFINER function that exposes only name + admin flag
    // instead of querying the table directly -- see migration 0099.
    const { data: driverRows, error: driversError } = await supabase
      .rpc('login_picker_drivers', { p_tenant_id: tenant.id });

    if (driversError) {
      setTenantError('Could not load drivers for this company. Please try again.');
      setResolvingTenant(false);
      return;
    }

    setTenantId(tenant.id);
    setTenantName(tenant.display_name || tenant.tenant_name);
    setTenantBrand(tenant);
    applyTheme(tenant);
    applyDocumentBranding({ title: tenant.display_name || tenant.tenant_name, faviconUrl: tenant.favicon_url });
    rememberTenantBranding(tenant);
    setTenantDrivers((driverRows as TenantDriverOption[]) || []);
    setResolvingTenant(false);
  };

  const handleChangeCompany = () => {
    setTenantBrand(null);
    applyTheme(null);
    applyDocumentBranding({ title: platform.product_name, faviconUrl: platform.favicon_url });
    rememberTenantBranding(null);
    setTenantId(null);
    setTenantName(null);
    setTenantDrivers([]);
    setTenantCode('');
    setName('');
    setPortalEmail('');
    setPassword('');
    setLoginMode('driver');
    setLoginError('');
  };

  const handleModeChange = (mode: 'driver' | 'partner' | 'admin') => {
    setLoginMode(mode);
    setName('');
    setPortalEmail('');
    setPassword('');
    setLoginError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !name.trim() || !password.trim() || submitting) return;

    setSubmitting(true);
    const success = await login(tenantId, name.trim(), password.trim());

    if (success) {
      const driver = drivers.find(d => d.driver_name === name.trim());
      if (driver) {
        // The Admin tile is an explicit choice, so AdminRights drivers land
        // straight in the Admin Dashboard. A Driver-tile login still checks
        // for an assigned permission group (e.g. the Leaseholder group for
        // a lease operator like Daren) so they land in the filtered Admin
        // Dashboard shell instead -- see effectivePermissions in AppContext
        // for how their view gets cut down from there. This check is a
        // quick lookup rather than waiting on effectivePermissions to
        // resolve (it hasn't yet -- that effect only fires after
        // currentUser is set, which just happened above).
        let role: 'driver' | 'admin' = 'driver';
        if (loginMode === 'admin') {
          role = 'admin';
        } else {
          let hasPermissionGroup = false;
          const { data: tuRow } = await supabase
            .from('tenant_users')
            .select('id')
            .eq('tenant_id', tenantId)
            .eq('driver_name', driver.driver_name)
            .eq('is_active', true)
            .maybeSingle();
          if (tuRow?.id) {
            const { data: groupRows } = await supabase
              .from('tenant_user_permission_groups')
              .select('id')
              .eq('tenant_user_id', tuRow.id)
              .eq('is_active', true)
              .limit(1);
            hasPermissionGroup = !!(groupRows && groupRows.length > 0);
          }
          role = hasPermissionGroup ? 'admin' : 'driver';
        }

        onLogin({
          name: driver.driver_name,
          role,
          vehicleId: driver.vehicleId
        });
        setLoginError('');
      }
    } else {
      setLoginError('🔒 Access denied.\nThat wasn’t quite it. Try again - you’ve got this!');
    }
    setSubmitting(false);
  };

  const handlePortalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !portalEmail.trim() || !password.trim() || submitting) return;

    setSubmitting(true);
    const result = await loginPortalUser(tenantId, portalEmail.trim(), password.trim());
    setSubmitting(false);

    if (result) {
      // Portal users always land in the (permission-filtered) Admin
      // Dashboard shell -- there's no drivers-table AdminRights concept
      // for them, and no DriverDashboard-style "my shift" experience
      // makes sense for a non-driver login.
      onLogin({ name: result.name, role: 'admin' });
      setLoginError('');
    } else {
      setLoginError('🔒 Access denied.\nThat wasn’t quite it. Try again - you’ve got this!');
    }
  };

  const handleDriverChange = (driverName: string) => {
    setName(driverName);
  };

  const handleSuperadminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!superadminEmail.trim() || !superadminPassword.trim() || superadminSubmitting) return;

    setSuperadminSubmitting(true);
    const success = await loginSuperadmin(superadminEmail.trim(), superadminPassword.trim());
    setSuperadminSubmitting(false);

    if (!success) {
      // Deliberately the same generic message regardless of whether the
      // credentials were wrong or the account just isn't a superadmin --
      // see loginSuperadmin's own comment for why.
      setSuperadminError('Access denied.');
    }
  };

  // Admin tile only lists admin-rights drivers. Driver tile lists
  // EVERYONE, admins included -- confirmed 2026-09-23 by Janelle: an
  // admin who also drives shifts needs to be able to sign in as a plain
  // driver too, not just via the Admin tile. (Lease operators like Daren
  // still get auto-routed into the filtered Admin Dashboard shell above
  // if they have a permission group, even when picked from the Driver
  // tile -- unaffected by this, that check is independent of AdminRights.)
  const driverOptions = tenantDrivers.filter((d) => (loginMode === 'admin' ? d.is_admin : true));

  // The entry point onto the superadmin form below -- Janelle's own
  // 8WireIQ logo, small and in the top-right corner rather than a fourth
  // tile next to Driver/Partner/Admin (an obvious "Superadmin" option
  // just invites regular users to try it). Unlike the earlier icon-only
  // version, this one is meant to actually be seen -- full opacity, just
  // small and off in its own corner. Rendered on both screens below via
  // renderSuperadminTrigger() so it's reachable before AND after a
  // company code is entered.
  const renderSuperadminTrigger = () => (
    <button
      type="button"
      onClick={() => setSuperadminMode(true)}
      aria-label="Superadmin"
      className="fixed top-4 right-4 rounded-full overflow-hidden w-9 h-9 shadow-lg ring-1 ring-white/20 hover:ring-white/60 transition-all hover:scale-105"
    >
      <img src={eightWireIqLogo} alt="" className="w-full h-full object-cover" />
    </button>
  );

  if (superadminMode) {
    return (
      <div className="min-h-screen flex items-center justify-center login-container p-4">
        <Card className="w-full max-w-md login-card">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-2xl font-bold golden-text mb-2">
              Superadmin
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSuperadminSubmit} className="space-y-6">
              <div>
                <Label htmlFor="superadminEmail" className="brand-label font-medium mb-2 block">
                  Email:
                </Label>
                <Input
                  id="superadminEmail"
                  type="email"
                  value={superadminEmail}
                  onChange={(e) => setSuperadminEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="dark-input h-12 text-lg"
                  autoFocus
                  required
                />
              </div>

              <div>
                <Input
                  id="superadminPassword"
                  type="password"
                  value={superadminPassword}
                  onChange={(e) => setSuperadminPassword(e.target.value)}
                  placeholder="Password"
                  className="dark-input h-12 text-lg"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full golden-button h-12 text-lg font-semibold"
                disabled={!superadminEmail || !superadminPassword || superadminSubmitting}
              >
                {superadminSubmitting ? 'Logging in...' : 'Login'}
              </Button>

              {superadminError && (
                <p className="text-center text-red-500 font-medium">{superadminError}</p>
              )}
            </form>

            <button
              type="button"
              onClick={() => {
                setSuperadminMode(false);
                setSuperadminEmail('');
                setSuperadminPassword('');
                setSuperadminError('');
              }}
              className="brand-link w-full text-center text-sm underline"
            >
              Back
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="min-h-screen flex items-center justify-center login-container platform-login p-4">
        <Card className="w-full max-w-md login-card">
          <CardHeader className="text-center pb-8">
            <img
              src={platform.logo_url || OPSLINK_ICON}
              alt=""
              className="mx-auto mb-4 max-h-24 max-w-[240px] object-contain"
            />
            <CardTitle className="platform-title text-5xl golden-text mb-2">
              {platform.product_name}
            </CardTitle>
            {platform.product_subtitle && <p className="platform-sub brand-sub text-lg">{platform.product_subtitle}</p>}
            <p className="platform-sub text-base mt-3 text-slate-300">
              {platform.tagline || 'Welcome! Enter your company code to get started.'}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleTenantSubmit} className="space-y-6">
              <div>
                <Label htmlFor="tenantCode" className="brand-label font-medium mb-2 block">
                  Company Code:
                </Label>
                <Input
                  id="tenantCode"
                  type="text"
                  value={tenantCode}
                  onChange={(e) => setTenantCode(e.target.value)}
                  placeholder="e.g. TG&B"
                  className="dark-input h-12 text-lg"
                  autoCapitalize="characters"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full golden-button h-12 text-lg font-semibold"
                disabled={!tenantCode.trim() || resolvingTenant}
              >
                {resolvingTenant ? 'Checking...' : 'Continue'}
              </Button>

              {tenantError && (
                <p className="text-center text-red-500 font-medium whitespace-pre-line mt-2">
                  {tenantError}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
        {renderSuperadminTrigger()}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center login-container p-4">
      <Card className="w-full max-w-md login-card">
        <CardHeader className="text-center pb-8">
          {tenantBrand?.logo_url && (
            <img src={tenantBrand.logo_url} alt="" className="mx-auto mb-4 max-h-16 max-w-[220px] object-contain" />
          )}
          <CardTitle className="text-4xl font-bold golden-text mb-2">
            {tenantName || platform.product_name}
          </CardTitle>
          <p className="brand-sub text-lg font-medium">{tenantBrand?.login_tagline || 'DMS'}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="brand-label font-medium mb-2 block">
              I'm signing in as
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { key: 'driver', label: 'Driver', Icon: User },
                  { key: 'partner', label: 'Partner', Icon: Handshake },
                  { key: 'admin', label: 'Admin', Icon: ShieldCheck },
                ] as const
              ).map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleModeChange(key)}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-md border py-3 text-xs font-medium transition-colors ${
                    loginMode === key
                      ? 'golden-button'
                      : 'brand-outline'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loginMode !== 'partner' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="brand-label font-medium mb-2 block">
                  Select {loginMode === 'admin' ? 'Admin' : 'Driver'}:
                </Label>
                {/* A plain native <select> rather than our usual styled
                    dropdown -- confirmed live 2026-10-02 that on Android,
                    scrolling a long name list inside the custom popup can
                    get misread as the page's own pull-to-refresh gesture,
                    reloading the whole app before a name can be tapped.
                    A native select hands the scrolling/picking UI to
                    Android itself, sidestepping that entirely -- most
                    important on exactly this screen, since it's the one
                    every driver hits first, on whatever phone they have. */}
                <select
                  id="name"
                  value={name}
                  onChange={(e) => handleDriverChange(e.target.value)}
                  className="dark-input dark-select flex h-12 w-full rounded-md border px-3 py-2 text-lg ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="" disabled>
                    Select {loginMode === 'admin' ? 'an admin' : 'a driver'}...
                  </option>
                  {driverOptions.map((driver) => (
                    <option key={driver.driver_name} value={driver.driver_name}>
                      {driver.driver_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="dark-input h-12 text-lg"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full golden-button h-12 text-lg font-semibold"
                disabled={!name || !password || submitting}
              >
                {submitting ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handlePortalSubmit} className="space-y-6">
              <div>
                <Label htmlFor="portalEmail" className="brand-label font-medium mb-2 block">
                  Email:
                </Label>
                <Input
                  id="portalEmail"
                  type="email"
                  value={portalEmail}
                  onChange={(e) => setPortalEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="dark-input h-12 text-lg"
                  required
                />
              </div>

              <div>
                <Input
                  id="portalPassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="dark-input h-12 text-lg"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full golden-button h-12 text-lg font-semibold"
                disabled={!portalEmail || !password || submitting}
              >
                {submitting ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          )}

          <button
            type="button"
            onClick={handleChangeCompany}
            className="brand-link w-full text-center text-sm underline"
          >
            Not {tenantName}? Change company code
          </button>

          {loginError && (
            <p className="text-center text-red-500 font-medium whitespace-pre-line mt-2">
              {loginError}
            </p>
          )}
        </CardContent>
      </Card>
      {renderSuperadminTrigger()}
    </div>
  );
};

export default LoginForm;
